'use client';

import Link from 'next/link';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';

export default function OfficePage() {
  return (
    <>
      <MarketingNav />

      <main className="pt-[56px]">
        {/* Hero */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-20 lg:py-28">
            <div className="max-w-[600px]">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-1.5">
                  <div className="w-8 h-8 rounded-[4px] bg-[#2B579A] flex items-center justify-center text-white text-[14px] font-bold">W</div>
                  <div className="w-8 h-8 rounded-[4px] bg-[#217346] flex items-center justify-center text-white text-[14px] font-bold">X</div>
                  <div className="w-8 h-8 rounded-[4px] bg-[#D24726] flex items-center justify-center text-white text-[14px] font-bold">P</div>
                </div>
              </div>
              <h1 className="text-[36px] sm:text-[48px] font-semibold text-[#1a1a1a] leading-[1.1] tracking-[-0.02em] mb-4">
                TekSpert Office
              </h1>
              <p className="text-[18px] text-[#555] leading-[1.65] mb-8">
                Create and edit documents, spreadsheets, and presentations — directly in your browser. Included free with every TekSpert Mail account. No separate software to install.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  href="/auth/register"
                  className="inline-flex px-6 py-3 bg-[#0078D4] text-white text-[15px] font-semibold rounded-[4px] hover:bg-[#005A9E] transition-colors"
                >
                  Get started free
                </Link>
                <Link
                  href="/features"
                  className="inline-flex px-6 py-3 bg-white text-[#333] text-[15px] font-semibold rounded-[4px] border border-[#d2d2d2] hover:bg-[#f5f5f5] transition-colors"
                >
                  View all features
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Applications */}
        <section className="bg-[#fafafa] border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-10">
              Three applications, one platform
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Documents */}
              <div className="p-6 bg-white rounded-[8px] border border-[#e5e5e5]">
                <div className="w-12 h-12 rounded-[6px] bg-[#E8EEF7] flex items-center justify-center mb-4">
                  <span className="text-[20px] font-bold text-[#2B579A]">W</span>
                </div>
                <h3 className="text-[18px] font-semibold text-[#1a1a1a] mb-2">Documents</h3>
                <p className="text-[15px] text-[#555] leading-[1.65] mb-4">
                  A full-featured word processor for writing letters, reports, proposals, and any other text document. Includes formatting tools, headers and footers, page numbering, spell check, and table insertion.
                </p>
                <ul className="space-y-2">
                  {[
                    'Rich text formatting (bold, italic, lists, headings)',
                    'Insert tables, images, and hyperlinks',
                    'Export to .docx, .odt, and .pdf',
                    'Spell check with English dictionaries',
                    'Real-time collaboration with other users',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[14px] text-[#555]">
                      <svg className="w-4 h-4 text-[#2B579A] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Spreadsheets */}
              <div className="p-6 bg-white rounded-[8px] border border-[#e5e5e5]">
                <div className="w-12 h-12 rounded-[6px] bg-[#E2F0E8] flex items-center justify-center mb-4">
                  <span className="text-[20px] font-bold text-[#217346]">X</span>
                </div>
                <h3 className="text-[18px] font-semibold text-[#1a1a1a] mb-2">Spreadsheets</h3>
                <p className="text-[15px] text-[#555] leading-[1.65] mb-4">
                  A powerful spreadsheet editor for budgets, data analysis, invoicing, and any task that involves working with numbers and structured data.
                </p>
                <ul className="space-y-2">
                  {[
                    'Full formula support (SUM, VLOOKUP, IF, etc.)',
                    'Cell formatting, conditional formatting, and filters',
                    'Charts and data visualisation',
                    'Export to .xlsx, .ods, and .csv',
                    'Multi-sheet workbooks',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[14px] text-[#555]">
                      <svg className="w-4 h-4 text-[#217346] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Presentations */}
              <div className="p-6 bg-white rounded-[8px] border border-[#e5e5e5]">
                <div className="w-12 h-12 rounded-[6px] bg-[#FCEAE6] flex items-center justify-center mb-4">
                  <span className="text-[20px] font-bold text-[#D24726]">P</span>
                </div>
                <h3 className="text-[18px] font-semibold text-[#1a1a1a] mb-2">Presentations</h3>
                <p className="text-[15px] text-[#555] leading-[1.65] mb-4">
                  Create professional slide presentations for meetings, pitches, and lectures. Add text, images, shapes, and transitions to bring your ideas to life.
                </p>
                <ul className="space-y-2">
                  {[
                    'Slide master templates and layouts',
                    'Insert images, shapes, and text boxes',
                    'Slide transitions and animations',
                    'Export to .pptx, .odp, and .pdf',
                    'Full-screen presentation mode',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[14px] text-[#555]">
                      <svg className="w-4 h-4 text-[#D24726] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Cross-platform */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Works everywhere you do
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  TekSpert Office runs in your web browser — no plugins, no downloads, no installation required. Open any document from your laptop, tablet, or phone and start editing immediately.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  The same editor is built into our native desktop applications for macOS and Windows. When you click a document in TekSpert Mail, it opens in the full editor right inside the app window.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  Your documents are stored securely on your TekSpert account and accessible from any device. Changes are saved automatically as you type.
                </p>
              </div>
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Compatible with Microsoft Office
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  TekSpert Office supports all major document formats natively. Open and edit .docx, .xlsx, and .pptx files without conversion issues. Your formatting, formulas, and layouts are preserved.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  You can also work with OpenDocument formats (.odt, .ods, .odp), CSV files, and export any document as a PDF for sharing with people who do not have an office application.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  Over 100 file formats are supported for import and export, so you can work with documents from virtually any source.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Included free */}
        <section className="bg-[#fafafa] border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
              Included free with TekSpert Mail
            </h2>
            <p className="text-[16px] text-[#555] leading-[1.65] max-w-[700px] mb-8">
              TekSpert Office is not a separate product — it is built into every TekSpert Mail account at no additional cost. There are no per-user fees, no document limits, and no premium tiers required to access the full suite.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { title: 'No extra cost', desc: 'Included free with every TekSpert Mail account. No add-ons to purchase.' },
                { title: 'No document limits', desc: 'Create as many documents, spreadsheets, and presentations as you need.' },
                { title: 'No installation', desc: 'Works in your browser and desktop app. Nothing to download or configure.' },
                { title: 'Full privacy', desc: 'Your documents are stored on our servers. We do not scan, index, or share your files.' },
              ].map((item) => (
                <div key={item.title} className="p-5 bg-white rounded-[8px] border border-[#e5e5e5]">
                  <h3 className="text-[15px] font-semibold text-[#1a1a1a] mb-1.5">{item.title}</h3>
                  <p className="text-[14px] text-[#555] leading-[1.55]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0078D4]">
          <div className="max-w-[800px] mx-auto px-6 py-14 text-center">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-white leading-tight mb-4">
              Start creating with TekSpert Office
            </h2>
            <p className="text-[16px] text-white/80 leading-[1.65] mb-6">
              Sign up for a free TekSpert Mail account and get instant access to Documents, Spreadsheets, and Presentations.
            </p>
            <Link
              href="/auth/register"
              className="inline-flex px-6 py-3 bg-white text-[#0078D4] text-[15px] font-semibold rounded-[4px] hover:bg-[#f5f5f5] transition-colors"
            >
              Create free account
            </Link>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}
